import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Check, AlertTriangle, Loader2, ShieldCheck, Store } from 'lucide-react';

const BACKEND_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_BACKEND_URL ?? '');
const API = `${BACKEND_URL}/api`;

type Status = 'idle' | 'capturing' | 'saving' | 'success' | 'error';

export default function SetLocationPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const capture = () => {
    setErrorMsg('');
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is invalid or missing. Generate a new one from the agent.');
      return;
    }
    if (!navigator.geolocation) {
      setStatus('error');
      setErrorMsg('Your device does not support location.');
      return;
    }
    setStatus('capturing');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setStatus('saving');
        try {
          const res = await fetch(`${API}/shop/settings/location-from-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              radiusMeters: 150,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            setStatus('error');
            setErrorMsg(data.error || 'Could not save. Generate a new link from the agent.');
            return;
          }
          setStatus('success');
        } catch {
          setStatus('error');
          setErrorMsg('Network error. Please try again.');
        }
      },
      (err) => {
        setStatus('error');
        setErrorMsg(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was denied. Tap Allow when asked, then try again.'
            : 'Could not get your location. Make sure GPS / Location is turned on, then try again.'
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const busy = status === 'capturing' || status === 'saving';

  // Icon tile colour per state
  const tile =
    status === 'success'
      ? 'from-emerald-500 to-emerald-700 shadow-emerald-600/25'
      : status === 'error'
      ? 'from-amber-500 to-orange-600 shadow-amber-600/25'
      : 'from-[#172554] to-[#1e40af] shadow-[#172554]/30';

  return (
    <div className="min-h-screen flex items-center justify-center p-5 antialiased">
      <div className="w-full max-w-[380px] rounded-[28px] bg-white/95 backdrop-blur border border-[#dbe7ff] shadow-[0_24px_60px_-20px_rgba(23,37,84,0.35)] px-7 pt-7 pb-8 text-center">
        {/* badge */}
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eef3ff] px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#1e3a8a]">
            <Store size={12} strokeWidth={2.4} /> Shop owner setup
          </span>
        </div>

        {/* icon tile — centered, animated ring */}
        <div className="relative mt-7 mb-1 flex justify-center">
          {!busy && status === 'idle' && (
            <span className="absolute top-1/2 left-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[#172554]/15 animate-ping" />
          )}
          <div
            className={`relative h-[72px] w-[72px] rounded-[22px] bg-gradient-to-br ${tile} flex items-center justify-center shadow-xl`}
          >
            {status === 'success' ? (
              <Check size={34} className="text-white" strokeWidth={3} />
            ) : status === 'error' ? (
              <AlertTriangle size={32} className="text-white" strokeWidth={2.4} />
            ) : busy ? (
              <Loader2 size={32} className="text-white animate-spin" />
            ) : (
              <MapPin size={34} className="text-white" strokeWidth={2.4} />
            )}
          </div>
        </div>

        {status === 'success' ? (
          <>
            <h1 className="mt-5 text-[21px] font-extrabold tracking-tight text-[#0f2f57]">
              Shop location saved
            </h1>
            <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: '#5b76a0' }}>
              Customers must now be at your shop to upload. You can close this page.
            </p>
          </>
        ) : status === 'error' ? (
          <>
            <h1 className="mt-5 text-[21px] font-extrabold tracking-tight text-[#0f2f57]">
              Couldn't save location
            </h1>
            <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: '#5b76a0' }}>
              {errorMsg}
            </p>
            <button
              type="button"
              onClick={capture}
              className="group mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#172554] to-[#1e40af] px-4 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-[#172554]/25 transition-transform active:scale-[0.98]"
            >
              <MapPin size={17} /> Try again
            </button>
          </>
        ) : (
          <>
            <h1 className="mt-5 text-[22px] font-extrabold tracking-tight text-[#0f2f57]">
              Set your shop's location
            </h1>
            <p className="mt-2.5 text-[13.5px] leading-relaxed" style={{ color: '#5b76a0' }}>
              Stand inside your shop and tap below — we'll pin this exact spot. Customers can only
              upload when they're here.
            </p>

            <div className="mt-5 flex items-start gap-2.5 rounded-2xl border border-emerald-200/70 bg-emerald-50 px-3.5 py-3 text-left">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <p className="text-[12.5px] leading-relaxed text-emerald-900/80">
                When your phone asks, tap <span className="font-bold text-emerald-900">Allow</span>.
                Keep GPS / Location on for the best accuracy.
              </p>
            </div>

            <button
              type="button"
              onClick={capture}
              disabled={busy}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#172554] to-[#1e40af] px-4 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-[#172554]/25 transition-transform active:scale-[0.98] disabled:opacity-70 disabled:active:scale-100"
            >
              {busy ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  {status === 'capturing' ? 'Pinpointing your shop…' : 'Saving…'}
                </>
              ) : (
                <>
                  <MapPin size={17} /> Capture &amp; save shop location
                </>
              )}
            </button>

            <p className="mt-3.5 text-[11px] text-[#8aa0c0]">
              Used only to verify customers are at your shop. Never stored otherwise.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
